import { useEffect, useState } from 'react';
import { locationApi } from '@/lib/api-paths';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/skeleton';
import { api, type ApiError } from '@/lib/api';
import { readyPrimaryVariant } from '@/lib/primary-action';
import { locationRoutes } from '@/lib/route-map';
import type { ExternalWebsiteState, ExternalWebsiteVerifyResponse } from '@/lib/types';

// =====================================================================
// External website card (site-lifecycle #19, Task 4 -- docs/plans/
// 2026-08-09-site-lifecycle-design.md's "DNS-proof three tiers" decision).
//
// Lets an owner who is keeping their EXISTING external website (not built
// on pageboss) prove they control it via a DNS TXT record, so the AEO audit
// can drop the "unverified" label and (seam closed 2026-08-10) feed its
// findings into the get-found action list. Tracking (geo/AI visibility) for
// an external site is still a future task. Setting this is entirely
// optional -- nothing here is required to use pageboss for GBP/reputation/
// tracking on top of an external site; the free unverified audit works
// either way.
//
// Consumes GET/POST/DELETE /api/sites/:id/external-website and POST
// /api/sites/:id/external-website/verify -- shapes mirrored from
// apps/dashboard/src/routes/api/site-verification.ts.
// =====================================================================

function DnsRow({ name, value }: { name: string; value: string }) {
  return (
    <div className="grid grid-cols-[6rem_1fr] gap-x-4 gap-y-1 rounded-md bg-card p-3 text-sm border border-border-subtle">
      <span className="text-text-tertiary">Type</span>
      <code className="font-mono">TXT</code>
      <span className="text-text-tertiary">Name</span>
      <code className="break-all font-mono">{name}</code>
      <span className="text-text-tertiary">Value</span>
      <code className="break-all font-mono">{value}</code>
    </div>
  );
}

export function ExternalWebsiteCard({
  siteId,
  primaryClaimed = false,
}: {
  siteId: string;
  /** True while the screen's header owns the single fill (Launch). */
  primaryClaimed?: boolean;
}) {
  // Canonical tenant URLs carry the Business id; these screens are only
  // ever rendered inside one, so it comes from the router, not a prop.
  const businessId = useBusinessId() ?? '';
  const [fetchState, setFetchState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [state, setState] = useState<ExternalWebsiteState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    let cancelled = false;
    setFetchState('loading');
    api
      .get<ExternalWebsiteState>(locationApi(businessId, siteId, 'external-website'))
      .then((res) => {
        if (cancelled) return;
        setState(res);
        setFetchState('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Could not load this setting.');
        setFetchState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  async function save() {
    const url = input.trim();
    if (!url) return;
    setSaving(true);
    setSaveError(null);
    setVerifyMessage(null);
    try {
      const res = await api.post<ExternalWebsiteState>(locationApi(businessId, siteId, 'external-website'), { url });
      setState(res);
      setInput('');
    } catch (err) {
      const e = err as ApiError;
      const code =
        e.body && typeof e.body === 'object' && 'error' in e.body
          ? (e.body as { error: string }).error
          : null;
      if (code === 'pageboss_host_not_external') {
        setSaveError(
          "That's a pageboss address, not an external one — this setting is for a website hosted somewhere else.",
        );
      } else if (e.status === 400) {
        setSaveError("That doesn't look like a website we can check. Use a normal https:// address.");
      } else {
        setSaveError(err instanceof Error ? err.message : 'Could not save that website.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function verify() {
    setVerifying(true);
    setVerifyMessage(null);
    try {
      const res = await api.post<ExternalWebsiteVerifyResponse>(
        locationApi(businessId, siteId, 'external-website/verify'),
      );
      if (res.verified) {
        setState((prev) => (prev ? { ...prev, verified: true, verifiedAt: res.verifiedAt ?? null } : prev));
        setVerifyMessage(null);
      } else {
        setVerifyMessage(
          "We couldn't find that record yet. DNS changes can take a few minutes to a few hours to show up.",
        );
      }
    } catch (err) {
      setVerifyMessage(err instanceof Error ? err.message : 'Could not check that record.');
    } finally {
      setVerifying(false);
    }
  }

  async function remove() {
    setRemoving(true);
    try {
      await api.delete(locationApi(businessId, siteId, 'external-website'));
      setState({ url: null, verified: false, verifiedAt: null, record: null });
      setVerifyMessage(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not remove this setting.');
    } finally {
      setRemoving(false);
    }
  }

  // Dirty = the field no longer matches the value that loaded. The editor
  // below only renders when nothing is stored yet (`!state.url`), so the
  // loaded value is the empty string — but deriving it from `state` rather
  // than hard-coding `!== ''` keeps the check correct if that branch ever
  // widens to editing an existing URL in place.
  const loadedUrl = (state?.url ?? '').trim();
  const dirty = input.trim() !== loadedUrl;

  return (
    <section id="external-website" className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">External website</h2>
        {/* Honest scope, restored (seam closed 2026-08-10 — docs/superpowers/
            specs/2026-08-10-aeo-findings-to-actions-design.md): verifying now
            unlocks your audit's to-do list, on top of dropping the
            "unverified" label — the AEO audit's findings feed the
            get-found action list once this domain is proven yours
            (deriveAeoRecommendations, gated on isVerifiedAuditTarget).
            Tracking (geo/AI visibility) for an external site is still not
            built, so it stays out of this promise. */}
        <p className="mt-1 text-sm text-muted-foreground">
          Keeping your existing website instead of one built here? Add a DNS record to prove you
          control this domain to unlock your audit&rsquo;s to-do list — verified audits feed your
          get-found action list, and your AEO audit reflects a site we&rsquo;ve confirmed is yours
          instead of an unverified one. This is optional — everything else still works without it.
        </p>
      </div>

      {fetchState === 'loading' ? <Skeleton className="h-24 w-full rounded-lg" /> : null}

      {fetchState === 'error' && loadError ? (
        <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{loadError}</p>
      ) : null}

      {fetchState === 'ready' && state && !state.url ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="yourbusiness.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && dirty && !saving) void save();
            }}
          />
          {/* Design audit T8 (A11, one primary per screen): this card sits on
              the Website screen, which already has its own primary (Launch,
              or "New page" once the site is live). The DNS save is outline
              while the field still matches what loaded — an empty field, since
              this branch only renders when no external website is stored — and
              becomes the primary the moment the owner has actually typed
              something, i.e. the moment it is the thing to press — unless
              the screen's header is already showing Launch, which outranks
              it while the site is in development mode (T14). */}
          <Button
            type="button"
            variant={readyPrimaryVariant(dirty, primaryClaimed)}
            disabled={saving || !dirty}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : null}

      {fetchState === 'ready' && state && state.url ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-surface-sunken px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground break-all">{state.url}</span>
              {state.verified ? (
                <Badge variant="success">Verified</Badge>
              ) : (
                <Badge variant="warning">Unverified</Badge>
              )}
            </div>
            <Button type="button" variant="outline" size="sm" disabled={removing} onClick={() => void remove()}>
              {removing ? 'Removing…' : 'Remove'}
            </Button>
          </div>

          {!state.verified && state.record ? (
            <div className="space-y-3">
              <p className="text-sm text-text-secondary">
                Add this TXT record at your domain's DNS provider, then check again. The name below
                isn't your domain itself — that's expected, it's a special record just for this
                check. This only proves you control the domain — it doesn't change anything about
                your site.
              </p>
              <DnsRow name={state.record.name} value={state.record.value} />
              <Button type="button" variant="outline" size="sm" disabled={verifying} onClick={() => void verify()}>
                {verifying ? 'Checking…' : 'Check now'}
              </Button>
              {verifyMessage ? <p className="text-sm text-text-secondary">{verifyMessage}</p> : null}
            </div>
          ) : null}

          {state.verified ? (
            <div className="space-y-2">
              <p className="text-sm text-text-secondary">
                This domain is proven yours. Want to point at a different site?{' '}
                <button
                  type="button"
                  className="underline"
                  onClick={() => setState((prev) => (prev ? { ...prev, url: null } : prev))}
                >
                  Change it
                </button>{' '}
                — you'll need to prove the new one too.
              </p>
              {/* Seam closed 2026-08-10 (deferred-polish Task 2 item 4): a
                  verified domain's audit findings already feed the get-found
                  list (see the file-level comment above) — one click here
                  closes that loop instead of leaving the owner to find the
                  list on their own. */}
              <Link
                to={locationRoutes.aeo(businessId, siteId)}
                className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
              >
                See your get-found to-do list
              </Link>
            </div>
          ) : null}
        </div>
      ) : null}

      {saveError ? <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{saveError}</p> : null}
    </section>
  );
}
